import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, SignOut, FileText, Trash, CaretRight, MapPin, UsersThree } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useSync } from "../context/SyncContext";
import SyncBadge from "../components/SyncBadge";
import { listInspections, saveInspection, deleteInspectionLocal, newId, nowIso } from "../lib/db";
import { api } from "../lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_d4fda698-50f5-4ffb-9202-1cc296025403/artifacts/9jmcuyor_UrbanConnect_RGB_Logo.png";

function statusPill(insp) {
  if (insp.dirty) return { label: "To sync", cls: "bg-warning/15 text-warning" };
  if (insp.synced) return { label: "Synced", cls: "bg-success/15 text-success" };
  return { label: "Draft", cls: "bg-steel/15 text-steel" };
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { runSync, refreshPending } = useSync();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setItems(await listInspections());
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  async function createNew() {
    const id = newId();
    const insp = {
      client_id: id,
      title: "",
      data: {},
      photos: [],
      status: "draft",
      created_at: nowIso(),
      updated_at: nowIso(),
      dirty: true,
      synced: false,
    };
    await saveInspection(insp);
    nav(`/inspection/${id}`);
  }

  async function remove(insp) {
    if (!window.confirm("Delete this inspection? This cannot be undone.")) return;
    await deleteInspectionLocal(insp.client_id);
    if (navigator.onLine) {
      try {
        await api.delete(`/inspections/${insp.client_id}`);
      } catch (e) {}
    }
    toast.success("Inspection deleted");
    load();
    refreshPending();
  }

  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-20 bg-white border-b-2 border-line">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <img src={LOGO} alt="Urban Connection" className="h-9" />
          <div className="flex items-center gap-2">
            <SyncBadge />
            {user?.role === "admin" && (
              <button
                data-testid="admin-btn"
                onClick={() => nav("/admin")}
                className="h-11 px-3 rounded-full border-2 border-navy text-navy flex items-center gap-1.5 font-head font-semibold text-sm"
              >
                <UsersThree size={20} weight="bold" /> Admin
              </button>
            )}
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="w-11 h-11 rounded-full border-2 border-line flex items-center justify-center text-steel"
              aria-label="Sign out"
            >
              <SignOut size={20} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="font-head text-2xl font-bold text-navydark tracking-tight">Inspections</h1>
            <p className="text-steel text-sm">Signed in as {user?.name || user?.email}</p>
          </div>
          <button data-testid="new-inspection-btn" onClick={createNew} className="uc-btn uc-btn-primary">
            <Plus size={20} weight="bold" /> New
          </button>
        </div>

        {loading ? (
          <p className="text-steel">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-lineheavy rounded-2xl">
            <FileText size={40} className="mx-auto text-steel mb-3" />
            <p className="text-steel mb-4">No inspections yet.</p>
            <button onClick={createNew} className="uc-btn uc-btn-teal" data-testid="empty-new-btn">
              <Plus size={20} weight="bold" /> Start an inspection
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((insp) => {
              const pill = statusPill(insp);
              const ct = insp.data?.crash_type;
              const loc = insp.data?.road_name || insp.data?.state_highway;
              return (
                <div
                  key={insp.client_id}
                  data-testid={`inspection-card-${insp.client_id}`}
                  onClick={() => nav(`/inspection/${insp.client_id}`)}
                  className="bg-white border-2 border-line rounded-2xl p-4 flex items-center gap-3 cursor-pointer hover:border-teal transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${pill.cls}`}>
                        {pill.label}
                      </span>
                      {ct && (
                        <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-navy/10 text-navy">
                          {ct}
                        </span>
                      )}
                    </div>
                    <p className="font-head font-semibold text-navydark truncate">
                      {insp.title || "Untitled inspection"}
                    </p>
                    <p className="text-sm text-steel truncate flex items-center gap-1">
                      {loc && (
                        <>
                          <MapPin size={14} weight="bold" /> {loc} &nbsp;•&nbsp;
                        </>
                      )}
                      {new Date(insp.updated_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    data-testid={`delete-btn-${insp.client_id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(insp);
                    }}
                    className="w-11 h-11 rounded-full flex items-center justify-center text-steel hover:text-danger"
                    aria-label="Delete"
                  >
                    <Trash size={20} weight="bold" />
                  </button>
                  <CaretRight size={20} className="text-lineheavy shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
